import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { defaultInstance } from 'src/api/instance';
import { configVars } from 'src/helpers';
import { IXrayAnalysis } from 'src/ts/interfaces/xray';

export type XrayStatus = 'idle' | 'loading' | 'success' | 'error';

export interface XrayAnalysisState {
  status: XrayStatus;
  data: IXrayAnalysis | null;
  error: string | null;
  retry: () => void;
}

type ProofResult = Omit<XrayAnalysisState, 'retry'>;

const IDLE: ProofResult = { status: 'idle', data: null, error: null };
const LOADING: ProofResult = { status: 'loading', data: null, error: null };
const FAILED: ProofResult = { status: 'error', data: null, error: 'X-Ray analysis failed' };

/**
 * Prefetches the x-ray analysis for every proof in the entry through the
 * fraud API proxy (ITL-3249) as soon as the review opens, so the X-Ray tab is
 * usually instant by the time the analyst reaches a document. One request per
 * proof id for the life of the component; results are held in memory only.
 * Unmounting aborts whatever is still in flight.
 *
 * Requests run one at a time, and the proof on screen always goes next, so an
 * entry with ten documents never puts ten concurrent analyses on the shared
 * outbound connection pool and the current document is never queued behind a
 * background prefetch.
 */
export const useXrayAnalysis = (
  proofIds: string[],
  currentProofId: string | undefined,
  enabled: boolean
): XrayAnalysisState => {
  const requestedRef = useRef<Set<string>>(new Set());
  const inFlightRef = useRef(false);
  const cancelSourceRef = useRef(axios.CancelToken.source());
  const [results, setResults] = useState<Record<string, ProofResult>>({});
  const [attempt, setAttempt] = useState(0);
  const [completed, setCompleted] = useState(0);

  useEffect(() => {
    const cancelSource = cancelSourceRef.current;
    return () => {
      cancelSource.cancel();
    };
  }, []);

  useEffect(() => {
    if (!enabled || inFlightRef.current) return;

    const requested = requestedRef.current;
    const nextId =
      currentProofId && !requested.has(currentProofId)
        ? currentProofId
        : proofIds.find((id) => id && !requested.has(id));
    if (!nextId) return;

    requested.add(nextId);
    inFlightRef.current = true;
    setResults((prev) => ({ ...prev, [nextId]: LOADING }));

    const fetchNext = async () => {
      let outcome: ProofResult;
      try {
        const body: { data: IXrayAnalysis } = await defaultInstance.get(
          `${configVars.server_url}/v2/proofs/${nextId}/xray`,
          { cancelToken: cancelSourceRef.current.token }
        );
        outcome = { status: 'success', data: body.data, error: null };
      } catch (error) {
        inFlightRef.current = false;
        if (axios.isCancel(error)) return;
        outcome = FAILED;
      }
      inFlightRef.current = false;
      setResults((prev) => ({ ...prev, [nextId]: outcome }));
      // Frees the queue so the effect picks up the next proof.
      setCompleted((previous) => previous + 1);
    };

    fetchNext();
  }, [proofIds, currentProofId, enabled, attempt, completed]);

  const retry = useCallback(() => {
    if (!currentProofId) return;
    requestedRef.current.delete(currentProofId);
    setAttempt((previous) => previous + 1);
  }, [currentProofId]);

  const current = (currentProofId && results[currentProofId]) || IDLE;

  return { ...current, retry };
};
